import { forwardRef, useImperativeHandle, useState, memo, useRef, useEffect } from 'react';
import RichTextEditor from './RichTextEditor';

/**
 * A RichTextEditor whose value is held in LOCAL state, isolated from the
 * parent's render cycle. This exists to fix a class of typing-lag bugs
 * where a RichTextEditor's `value` lives on a big parent form (leads,
 * contracts, RFPs, project meeting notes, etc). In that setup each keystroke
 * re-rendered the entire panel/page, and users saw letters appear in bursts
 * with lag between them.
 *
 * Usage:
 *   const ref = useRef(null);
 *   <IsolatedRichTextField ref={ref} initialValue={form.notes} ... />
 *   // At submit time:
 *   const notes = ref.current?.getValue() ?? '';
 *   await api.put(..., { ...form, notes });
 *
 * The parent no longer stores per-keystroke value; it just reads the current
 * value on save. If the parent needs to reset the field (e.g. switching
 * records), bump the `resetKey` prop and the field re-seeds from
 * `initialValue`.
 */
const IsolatedRichTextField = forwardRef(function IsolatedRichTextField(
  { initialValue = '', resetKey, ...rest }, ref
) {
  const [value, setValue] = useState(initialValue || '');
  const valueRef = useRef(value);
  valueRef.current = value;

  // Re-seed when caller signals a reset (e.g., editing a different record).
  // We intentionally exclude `initialValue` from deps: the caller uses
  // `resetKey` to signal a real switch; treating initialValue as a dep would
  // re-seed on every parent render and destroy user input.
  useEffect(() => {
    setValue(initialValue || '');
    valueRef.current = initialValue || '';
  }, [resetKey]);

  useImperativeHandle(ref, () => ({
    getValue: () => valueRef.current,
    setValue: (v) => {
      const next = v ?? '';
      setValue(next);
      valueRef.current = next;
    },
  }), []);

  return <RichTextEditor value={value} onChange={setValue} {...rest} />;
});

export default memo(IsolatedRichTextField);
